import {
    Badge,
    Box,
    Button,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Spinner,
    Text,
    Tooltip,
    useClipboard,
    useColorModeValue,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { MdContentCopy, MdGroup, MdLink, MdRefresh } from "react-icons/md";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useGetWhatsAppGroups, WhatsAppGroup } from "../hooks/useGetWhatsAppGroups";
import { useGetWhatsAppConfig } from "../hooks/useGetWhatsAppConfig";
import { useCreateWhatsAppGroup } from "../hooks/useCreateWhatsAppGroup";
import { useGetWhatsAppGroupInviteLink } from "../hooks/useGetWhatsAppGroupInviteLink";
import InlineErrorState from "../../../components/states/InlineErrorState";
import { classifyRead, READ_FAILED_I18N_KEY } from "../../_shared/readState";

function GroupInviteLinkButton({ organizationId, group }: { organizationId: string; group: WhatsAppGroup }) {
    const toast = useToast();
    const [enabled, setEnabled] = useState(false);
    const {
        data,
        isFetching,
        isError,
        isInitialLoading,
    } = useGetWhatsAppGroupInviteLink(organizationId, group.id, enabled);
    // Routed through `classifyRead` rather than read off `isError` directly, so
    // this read carries its own evidence instead of resting on a cover: its gate
    // is `!!organizationId && !!groupId && enabled`, and `enabled` is local
    // state, so NO early return on this page can prove it was issued. The
    // never-issued arm is the "Get link" button below — the correct affordance
    // for a read nobody has asked for yet (the run-101 CommandPalette finding),
    // which is why this is a conversion and not a fix.
    const linkReadState = classifyRead(isError, isInitialLoading, data);
    const { onCopy, hasCopied } = useClipboard(data?.inviteLink ?? '');

    if (linkReadState === 'unreadable') {
        return (
            <Text fontSize="xs" color="red.400">
                {translate('organization.whapi_groups_invite_link_error' as any)}
            </Text>
        );
    }

    if (data?.inviteLink) {
        return (
            <HStack spacing={1}>
                <Text fontSize="xs" color="blue.400" isTruncated maxW="200px">
                    {data.inviteLink}
                </Text>
                <Tooltip label={hasCopied ? "Copied!" : translate('organization.whapi_groups_copy_link' as any)}>
                    <IconButton
                        aria-label="Copy invite link"
                        icon={<MdContentCopy />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                            onCopy();
                            toast({
                                title: translate('organization.whapi_groups_invite_link_copied' as any),
                                status: 'success',
                                duration: 2000,
                            });
                        }}
                    />
                </Tooltip>
            </HStack>
        );
    }

    return (
        <Button
            size="xs"
            variant="outline"
            leftIcon={<MdLink />}
            onClick={() => setEnabled(true)}
            isLoading={isFetching}
        >
            {translate('organization.whapi_groups_get_link' as any)}
        </Button>
    );
}

export default function WhatsAppGroupsManager() {
    const { selectedOrganization } = useSelectedOrganization();
    const toast = useToast();
    const cardBg = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');

    const orgId = selectedOrganization?.id || '';
    // The sibling `WhatsAppConfigManager` reads the same hook WITH its error
    // flag and renders a proper error arm; this copy took no flag at all, so
    // `hasWhatsAppConfig` was `undefined && …` → falsy for a refused read, for a
    // never-issued one, and for every first paint — and the falsy branch below
    // states, in orange, that the venue has NO WhatsApp token configured. That
    // is a claim about the organization's configuration made from a read that
    // did not come back, and it points the operator at re-entering a token they
    // already have.
    const {
        data: config,
        isError: isConfigError,
        isInitialLoading: isConfigInitialLoading,
    } = useGetWhatsAppConfig(orgId);
    const configReadState = classifyRead(isConfigError, isConfigInitialLoading, config);
    const hasWhatsAppConfig = config?.hasAccessToken && !!config?.whatsappPhoneNumberId;

    const [loadGroups, setLoadGroups] = useState(false);
    // This read took NO error flag at all until run 124, and it is the one the
    // census could not see: `useGetWhatsAppGroups` gates on
    // `!!organizationId && enabled`, but the hook's own signature carries a
    // parameter called `enabled`, so the detector captured the type annotation
    // instead of the gate and the hook sat outside the scoped population.
    // Behind that hole: an operator presses "Load groups", the button's spinner
    // stops, and NOTHING appears — no list, no empty line, no error, because
    // both render branches below were keyed off `groups` being truthy and a
    // refused read leaves it `undefined`. The button looks broken rather than
    // refused, which is D58's silent vanish.
    const {
        data: groups,
        isFetching: isLoadingGroups,
        isError: isGroupsError,
        isInitialLoading: isGroupsInitialLoading,
        refetch,
    } = useGetWhatsAppGroups(orgId, loadGroups && !!hasWhatsAppConfig);
    // `isInitialLoading`, not `isFetching`: the button above already owns the
    // in-flight affordance, and classifying on `isFetching` would blank a good
    // list every time the operator pressed Refresh (R96-3, the mirror defect).
    const groupsReadState = classifyRead(isGroupsError, isGroupsInitialLoading, groups);

    const { mutateAsync: createGroup, isLoading: isCreating } = useCreateWhatsAppGroup(orgId);

    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupParticipants, setNewGroupParticipants] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    if (!selectedOrganization) return null;

    const handleLoadGroups = () => {
        setLoadGroups(true);
        if (loadGroups) {
            refetch();
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;

        const participants = newGroupParticipants
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean);

        try {
            const result = await createGroup({
                subject: newGroupName.trim(),
                participants: participants.length > 0 ? participants : undefined,
            });

            toast({
                title: translate('organization.whapi_groups_create_success' as any),
                description: result.inviteLink ? `Invite: ${result.inviteLink}` : undefined,
                status: 'success',
                duration: 4000,
            });

            setNewGroupName('');
            setNewGroupParticipants('');
            setShowCreateForm(false);

            if (loadGroups) refetch();
        } catch {
            toast({
                title: translate('organization.whapi_groups_create_error' as any),
                status: 'error',
                duration: 3000,
            });
        }
    };

    return (
        <PageCard title={translate('organization.whapi_groups_title' as any)}>
            <Box p={4}>
                <Text mb={4} color={textColorPrimary} fontSize="sm">
                    {translate('organization.whapi_groups_description' as any)}
                </Text>

                {configReadState === 'loading' ? (
                    <Spinner size="sm" />
                ) : configReadState !== 'answered' ? (
                    // Not the orange "no token configured" line: we do not know
                    // whether a token is configured. Saying so is the only
                    // honest option, and `InlineErrorState` is the campaign's
                    // compact in-flow notice for exactly that (D59 — reused,
                    // never re-styled). The key is borrowed rather than added:
                    // `src/i18n/{en,fr}.ts` are inside the open-PR fence
                    // (R96-7).
                    <InlineErrorState message={translate(READ_FAILED_I18N_KEY as any)} />
                ) : !hasWhatsAppConfig ? (
                    <Text color="orange.400" fontSize="sm">
                        {translate('organization.whapi_groups_no_token' as any)}
                    </Text>
                ) : (
                    <VStack spacing={4} align="stretch">
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                leftIcon={<MdRefresh />}
                                onClick={handleLoadGroups}
                                isLoading={isLoadingGroups}
                            >
                                {loadGroups ? translate('common.refresh' as any) : translate('organization.whapi_load_groups' as any)}
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="brand"
                                leftIcon={<MdGroup />}
                                onClick={() => setShowCreateForm(!showCreateForm)}
                            >
                                {translate('organization.whapi_groups_create' as any)}
                            </Button>
                        </HStack>

                        {showCreateForm && (
                            <Box
                                p={4}
                                borderWidth="1px"
                                borderColor={borderColor}
                                borderRadius="md"
                                bg={cardBg}
                            >
                                <VStack spacing={3} align="stretch">
                                    <Box>
                                        <FormLabel fontSize="sm">
                                            {translate('organization.whapi_groups_create_name' as any)}
                                        </FormLabel>
                                        <Input
                                            size="sm"
                                            value={newGroupName}
                                            onChange={(e) => setNewGroupName(e.target.value)}
                                            placeholder={translate('organization.whapi_groups_create_name_placeholder' as any)}
                                        />
                                    </Box>
                                    <Box>
                                        <FormLabel fontSize="sm">
                                            {translate('organization.whapi_groups_create_participants' as any)}
                                        </FormLabel>
                                        <Input
                                            size="sm"
                                            value={newGroupParticipants}
                                            onChange={(e) => setNewGroupParticipants(e.target.value)}
                                            placeholder={translate('organization.whapi_groups_create_participants_placeholder' as any)}
                                        />
                                    </Box>
                                    <HStack>
                                        <Button
                                            size="sm"
                                            colorScheme="brand"
                                            onClick={handleCreateGroup}
                                            isLoading={isCreating}
                                            isDisabled={!newGroupName.trim()}
                                        >
                                            {translate('organization.whapi_groups_create' as any)}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setShowCreateForm(false)}
                                        >
                                            {translate('common.cancel')}
                                        </Button>
                                    </HStack>
                                </VStack>
                            </Box>
                        )}

                        {groupsReadState === 'unreadable' && (
                            <InlineErrorState message={translate(READ_FAILED_I18N_KEY as any)} />
                        )}

                        {groupsReadState === 'answered' && groups && groups.length === 0 && (
                            <Text color="gray.500" fontSize="sm">
                                {translate('organization.whapi_groups_empty' as any)}
                            </Text>
                        )}

                        {groupsReadState === 'answered' && groups && groups.length > 0 && (
                            <VStack spacing={2} align="stretch">
                                {groups.map((group) => (
                                    <Box
                                        key={group.id}
                                        p={3}
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                        borderRadius="md"
                                        bg={cardBg}
                                    >
                                        <HStack justify="space-between" align="start" wrap="wrap" spacing={2}>
                                            <VStack align="start" spacing={1} flex={1} minW={0}>
                                                <HStack spacing={2}>
                                                    <Text fontWeight="medium" fontSize="sm" isTruncated>
                                                        {group.name}
                                                    </Text>
                                                    <Badge colorScheme="gray" fontSize="xs">
                                                        {group.memberCount} {translate('organization.whapi_groups_members' as any)}
                                                    </Badge>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.500" isTruncated>
                                                    {group.id}
                                                </Text>
                                            </VStack>
                                            <GroupInviteLinkButton
                                                organizationId={orgId}
                                                group={group}
                                            />
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </VStack>
                )}
            </Box>
        </PageCard>
    );
}
